import React, { useCallback } from 'react'
import { Button, Dimmer, Icon, Loader, Message } from 'semantic-ui-react'
import PropTypes from 'prop-types'
import '../SuperCutoff.css'

// ID per aria-describedby — evita stringhe magiche nel JSX
const ID_STATO_CORRENTE = 'stepcrtoggle-stato-corrente'

// Configurazione testuale per modalità — elimina la logica ternaria ripetuta nel JSX
const MODAL_CONFIG = {
  block: {
    descrizione: 'Disables the creation of new Change Requests (CR) across all projects. This prevents new CRs from being opened during the SuperCutoff process.',
    conferma: 'This action will immediately prevent all users from creating new CRs. Confirm only when ready to start the SuperCutoff.',
    successo: 'CR creation disabled successfully',
    bottoneLabel: 'Block CR creation',
    bottoneIcona: 'lock',
    bottoneColore: 'red',
  },
  unblock: {
    descrizione: 'Re-enables the creation of Change Requests (CR) across all projects. Execute this step only after the SuperCutoff closure is fully completed.',
    conferma: 'This action will re-enable CR creation for all users. Confirm only after the SuperCutoff closure is complete.',
    successo: 'CR creation re-enabled successfully',
    bottoneLabel: 'Unblock CR creation',
    bottoneIcona: 'lock open',
    bottoneColore: 'green',
  },
}

/**
 * Step 1 — Blocco CR / Step 10 — Sblocco CR.
 * Mostra lo stato attuale del parametro crAvailable e permette di modificarlo.
 *
 * Props:
 *   mode          {string}    'block' per bloccare, 'unblock' per sbloccare
 *   crAvailable   {boolean}   Stato attuale del parametro (true = CR abilitate). Default: false
 *   onExecute     {Function}  Callback per eseguire il toggle. Firma: fn()
 *   loading       {boolean}   [opzionale] true durante la chiamata API di toggle. Default: false
 *   loadingStatus {boolean}   [opzionale] true durante la fetch iniziale dello stato CR. Default: false
 *   result        {object}    [opzionale] Risultato dell'operazione.
 *                             result.updatedAt {string} Data in formato ISO 8601 o testo formattato
 */
const StepCrToggle = ({ mode, crAvailable = false, onExecute, loading = false, loadingStatus = false, result }) => {
  const config = MODAL_CONFIG[mode]

  // useCallback per coerenza con React.memo sull'export
  const handleExecute = useCallback(() => {
    // guard difensivo: PropTypes.isRequired non blocca a runtime
    if (!onExecute) return
    onExecute()
  }, [onExecute])

  // Loading iniziale: finché lo stato reale delle CR non è caricato, mostra
  // un loader al posto dell'etichetta ENABLED/DISABLED. Nasconde anche box di
  // conferma e bottone, così l'utente non agisce su uno stato non ancora noto.
  if (loadingStatus) {
    return (
      <div>
        <p className="supercutoff-step-description">{config.descrizione}</p>
        <div className="supercutoff-preview-box">
          <h4>Current status</h4>
          <Dimmer active inverted>
            <Loader inverted content="Loading CR status" size="medium" />
          </Dimmer>
        </div>
      </div>
    )
  }

  return (
    <div>
      <p className="supercutoff-step-description">{config.descrizione}</p>

      {/* Stato attuale — collegato al bottone tramite aria-describedby */}
      <div className="supercutoff-preview-box">
        <h4>Current status</h4>
        <div id={ID_STATO_CORRENTE} className="supercutoff-cr-status-row">
          <Icon
            name={crAvailable ? 'check circle' : 'times circle'}
            color={crAvailable ? 'green' : 'red'}
            size="large"
            aria-hidden="true"
          />
          <span className="supercutoff-cr-status-label">
            CR creation is currently <strong>{crAvailable ? 'ENABLED' : 'DISABLED'}</strong>
          </span>
        </div>
      </div>

      {/*
        Due elementi aria-live separati per evitare che la sostituzione strutturale
        del contenuto non venga annunciata correttamente dagli screen reader.
      */}
      <div aria-live="polite">
        {!result && (
          <div className="supercutoff-confirm-box">
            <Icon name="warning sign" color="yellow" size="large" aria-hidden="true" />
            <span className="confirm-text">{config.conferma}</span>
          </div>
        )}
      </div>

      <div aria-live="polite">
        {result && (
          <Message positive icon>
            <Icon name="check circle" aria-hidden="true" />
            <Message.Content>
              <Message.Header>{config.successo}</Message.Header>
              {/* Fallback 'N/A' se updatedAt non è presente nell'oggetto result */}
              Updated at: {result.updatedAt || 'N/A'}
            </Message.Content>
          </Message>
        )}
      </div>

      <div className="supercutoff-step-actions">
        {!result && (
          <Button
            color={config.bottoneColore}
            icon
            labelPosition="left"
            onClick={handleExecute}
            loading={loading}
            disabled={loading}
            aria-describedby={ID_STATO_CORRENTE}
          >
            <Icon name={config.bottoneIcona} aria-hidden="true" />
            {config.bottoneLabel}
          </Button>
        )}
        {result && (
          <span className="supercutoff-status-badge completed" role="status">
            <Icon name="check" aria-hidden="true" />
            Step completed
          </span>
        )}
      </div>
    </div>
  )
}

StepCrToggle.propTypes = {
  mode: PropTypes.oneOf(['block', 'unblock']).isRequired,
  crAvailable: PropTypes.bool.isRequired,
  onExecute: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  loadingStatus: PropTypes.bool,
  result: PropTypes.shape({ updatedAt: PropTypes.string }),
}

export default React.memo(StepCrToggle)
